import type { ActiveChatSession, InitParams, WidgetRuntimeState } from '@wix/wix-help-widget-common/types';

import type { DealerOfferDetails } from '../dealer';
import {
  subscribeToAppRender,
  subscribeToNavigation,
  subscribeToPostMessage,
  subscribeToWidgetStateChange,
} from '../events/subscribe';
import { WidgetSDKHandler } from '../events/WidgetSDKHandler';
import { renderWidget } from '../render';
import {
  chatbotSessionService,
  DealerOfferService,
  getWidgetSession,
  InitWidgetService,
  localizationService,
  updateWidgetSession,
} from '../services';
import { WidgetRuntimeService } from '../services/WidgetRuntimeService';
import { dispatch, getState, onStoreEvent } from '../store';
import type { UpdateWidgetSessionResponse } from '../types';
import type { IWidget } from '../widget';
import {
  publishContext,
  publishNewDealerOfferParams,
  publishOrigParams,
  publishStartUrl,
} from '../widget/publishers';
import { buildHelpWidgetStartUrl } from '../widget/startUrl';
import { widgetModel } from '../widgetModel';

// Responsible for the help widget lifecycle when we are rendering the full help experience.
export class HelpWidget implements IWidget {
  private initWidgetService: InitWidgetService;
  private widgetRuntimeService: WidgetRuntimeService;
  private dealerOfferService: DealerOfferService;
  private updateWidgetSessionPromise: Promise<UpdateWidgetSessionResponse>;
  private iframeLoaded: boolean = false;

  constructor(initParams: InitParams) {
    if (!initParams || !initParams.origin) {
      throw new Error('Must pass an origin');
    }
    // setting the store with init params only once
    // we should find a better solution, if there is...
    dispatch('app/initParams', initParams);
    this.initWidgetService = new InitWidgetService();
    this.widgetRuntimeService = new WidgetRuntimeService();
    this.dealerOfferService = new DealerOfferService({ origin: initParams.origin });
  }

  private setWidgetIframeUrl(): void {
    const { chatbot } = getState();
    const iframeUrl = buildHelpWidgetStartUrl(chatbot.chatbotActiveSession);
    dispatch('app/setWidgetIframeSrc', iframeUrl);
  }

  async initialize(): Promise<void> {
    await Promise.all([localizationService.init(widgetModel.getLocale()), this.initWidgetService.fetch()]);
    this.setWidgetIframeUrl();
  }

  async refetchInitialData(): Promise<void> {
    await this.initWidgetService.fetch();
  }

  registerSubscribers(): void {
    subscribeToAppRender();
    subscribeToNavigation(this.handleNavigation);
    subscribeToPostMessage(new WidgetSDKHandler(this));
    subscribeToWidgetStateChange((data) => {
      this.updateWidgetSessionPromise = updateWidgetSession(data);
    });
    this.subscribeToWidgetMount();
  }

  render(): void {
    renderWidget();
  }

  getRuntimeState(): Promise<WidgetRuntimeState | undefined> {
    return this.widgetRuntimeService.get();
  }

  getDealerOfferDetails(): Promise<DealerOfferDetails | undefined> {
    return this.dealerOfferService.get();
  }

  getChatbotActiveSession(): ActiveChatSession | undefined {
    return getState().chatbot.chatbotActiveSession;
  }

  refreshRuntimeState(): void {
    this.widgetRuntimeService.refresh();
  }

  private handleNavigation = async (): Promise<void> => {
    dispatch('app/pageNavigate');

    const [widgetSession, activeChatSession] = await Promise.all([
      this.updateWidgetSessionPromise ? this.updateWidgetSessionPromise : getWidgetSession(),
      chatbotSessionService.fetchActiveSession(),
      this.iframeLoaded && this.dealerOfferService.fetch(),
    ]);

    dispatch('chatbot/setSession', { activeChatSession, widgetSession });
    dispatch('widget/setSession', widgetSession);

    await Promise.all([
      publishStartUrl(),
      this.iframeLoaded && publishNewDealerOfferParams(),
      publishContext(),
      publishOrigParams(),
    ]);

    this.render();
  };

  private subscribeToWidgetMount = (): void => {
    onStoreEvent('@events/widget/iframeSrcSet', () => {
      this.dealerOfferService.fetch();
    });

    // use iframeLoaded because in case if iframe is failed we will not fetch offer on each navigation(decreasing API calls)
    onStoreEvent('@events/widget/iframeLoaded', () => {
      this.iframeLoaded = true;
    });
  };
}
